import { Alert, Paper, Typography } from "@mui/material";
import type { GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import { DataGridPro } from "@mui/x-data-grid-pro";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { Scrollbar } from "../../../../generic/components/scrollbar";
import type { CustomHeader } from "../../server/supabase-custom-email-header-service";
import { DeleteHeaderButton } from "./delete-header-button";
import { EditHeaderButton } from "./edit-header-button";
import { UpsertHeaderDialog } from "./upsert-header-dialog";

export const EmailHeaderTable: FC<{
  customHeaders: CustomHeader[];
  reload: () => void;
}> = ({ customHeaders, reload }) => {
  const [editingHeader, setEditingHeader] = useState<CustomHeader | null>(null);

  const columns: GridColDef<CustomHeader>[] = useMemo(
    () => [
      {
        field: "display_name",
        headerName: "Display Name",
        flex: 1,
      },
      {
        field: "header_name",
        headerName: "Header Name",
        flex: 1,
      },
      {
        field: "header_value",
        headerName: "Header Value",
        flex: 1,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.5,
        type: "actions",
        getActions: (params: GridRowParams<CustomHeader>) => [
          <EditHeaderButton
            key="edit"
            customHeader={params.row}
            onClick={() => setEditingHeader(params.row)}
          />,
          <DeleteHeaderButton key="delete" customHeader={params.row} onDelete={reload} />,
        ],
      },
    ],
    [reload],
  );

  if (customHeaders.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          No custom headers configured yet. Add one above to get started.
        </Typography>
      </Alert>
    );
  }

  return (
    <Scrollbar>
      <Paper sx={{ mt: 2, width: "100%" }}>
        <DataGridPro
          rows={customHeaders}
          getRowId={(row) => row.id}
          columns={columns}
          disableRowSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [{ field: "display_name", sort: "asc" }],
            },
          }}
        />
      </Paper>
      {editingHeader && (
        <UpsertHeaderDialog
          open={true}
          onClose={() => setEditingHeader(null)}
          onSave={reload}
          customHeader={editingHeader}
        />
      )}
    </Scrollbar>
  );
};
